import React, { useContext, useMemo, useState } from 'react';
import { Button, DatePicker, TimeRangePickerProps, Tooltip } from 'antd';
import { RedoOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { FormattedMessage, useIntl } from 'react-intl';
import { AppContext } from 'contexts/AppContext';
import { useGetInsights } from 'hooks/insights';
import { namespaceMetricKeys } from 'types/insights';
import { normalizeMetricsSeries, toPercentNumber } from 'utils/insights';
import UtilizationCard from './UtilizationCard/UtilizationCard';
import './Overview.scss';

dayjs.extend(utc);
dayjs.extend(timezone);

const Overview: React.FC = () => {
  const intl = useIntl();
  const { clusterName } = useContext(AppContext);

  const defaultFromDate = dayjs().subtract(24, 'hour');
  const defaultToDate = dayjs();

  const [dateRangePicker, setDateRangePicker] = useState<
    [dayjs.Dayjs, dayjs.Dayjs]
  >([defaultFromDate, defaultToDate]);

  const startTime = dateRangePicker[0]?.toISOString() ?? '';
  const endTime = dateRangePicker[1]?.toISOString() ?? '';

  const namespace = clusterName ? `ic-${clusterName}` : '';

  const {
    data: insights,
    isFetching,
    error,
    refetch,
    dataUpdatedAt,
  } = useGetInsights({
    clusterName,
    startTime,
    endTime,
  });

  const rangePresets: TimeRangePickerProps['presets'] = [
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past15Minutes' }),
      value: [dayjs().subtract(15, 'minute'), dayjs()],
    },
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past1Hour' }),
      value: [dayjs().subtract(1, 'hour'), dayjs()],
    },
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past6Hours' }),
      value: [dayjs().subtract(6, 'hour'), dayjs()],
    },
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past12Hours' }),
      value: [dayjs().subtract(12, 'hour'), dayjs()],
    },
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past24Hours' }),
      value: [dayjs().subtract(24, 'hour'), dayjs()],
    },
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past7Days' }),
      value: [dayjs().subtract(7, 'day'), dayjs()],
    },
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past14Days' }),
      value: [dayjs().subtract(14, 'day'), dayjs()],
    },
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past30Days' }),
      value: [dayjs().subtract(30, 'day'), dayjs()],
    },
  ];

  const handleDateChange: TimeRangePickerProps['onChange'] = (values) => {
    if (values && values[0] && values[1]) {
      setDateRangePicker([values[0], values[1]]);
    }
  };

  // FIXED: Collapsed into a clean, single-memo processing block to fix ts(7053)
  const {
    cpuLimitSeries,
    cpuRequestSeries,
    memoryLimitSeries,
    memoryRequestSeries,
  } = useMemo(() => {
    const rawData = insights || {};
    return {
      cpuLimitSeries: normalizeMetricsSeries(rawData[namespaceMetricKeys.cpuLimit] || []),
      cpuRequestSeries: normalizeMetricsSeries(rawData[namespaceMetricKeys.cpuRequest] || []),
      memoryLimitSeries: normalizeMetricsSeries(rawData[namespaceMetricKeys.memoryLimit] || []),
      memoryRequestSeries: normalizeMetricsSeries(rawData[namespaceMetricKeys.memoryRequest] || []),
    };
  }, [insights]);

  const cpuTimeData =
    cpuLimitSeries.timeData.length > 0
      ? cpuLimitSeries.timeData
      : cpuRequestSeries.timeData;

  const memoryTimeData =
    memoryLimitSeries.timeData.length > 0
      ? memoryLimitSeries.timeData
      : memoryRequestSeries.timeData;

  const cpuSeries = [
    {
      name: intl.formatMessage({ id: 'overview.series.limitUtilization' }),
      data: cpuLimitSeries.values.map(toPercentNumber),
    },
    {
      name: intl.formatMessage({ id: 'overview.series.requestUtilization' }),
      data: cpuRequestSeries.values.map(toPercentNumber),
    },
  ];

  const memorySeries = [
    {
      name: intl.formatMessage({ id: 'overview.series.limitUtilization' }),
      data: memoryLimitSeries.values.map(toPercentNumber),
    },
    {
      name: intl.formatMessage({ id: 'overview.series.requestUtilization' }),
      data: memoryRequestSeries.values.map(toPercentNumber),
    },
  ];

  return (
    <section className="overview">
      <div className="overview__filters">
        <DatePicker.RangePicker
          className="overview__filters-range"
          presets={rangePresets}
          value={dateRangePicker}
          onChange={handleDateChange}
          allowClear={false}
          showTime
        />
        <span className="overview__filters-spacer" />
        <span className="overview__filters-sync">
          <FormattedMessage
            id="table.lastSync"
            values={{
              time: dataUpdatedAt
                ? format(new Date(dataUpdatedAt), 'hh:mm:ss a')
                : '',
            }}
          />
        </span>
        <Tooltip
          title={intl.formatMessage({ id: 'overview.refresh' })}
          placement="topRight">
          <Button
            icon={<RedoOutlined rotate={-90} />}
            className="overview__filters-refresh"
            onClick={() => refetch()}
            loading={isFetching}
          />
        </Tooltip>
      </div>

      <div className="overview__cards">
        <UtilizationCard
          title={intl.formatMessage({ id: 'overview.cpu.title' })}
          subtitle={intl.formatMessage(
            { id: 'overview.subtitle' },
            { namespace }
          )}
          series={cpuSeries}
          timeData={cpuTimeData}
          loading={isFetching && !insights}
          error={error}
          onRetry={() => refetch()}
        />

        <UtilizationCard
          title={intl.formatMessage({ id: 'overview.memory.title' })}
          subtitle={intl.formatMessage(
            { id: 'overview.subtitle' },
            { namespace }
          )}
          series={memorySeries}
          timeData={memoryTimeData}
          loading={isFetching && !insights}
          error={error}
          onRetry={() => refetch()}
        />
      </div>
    </section>
  );
};

export default Overview;